import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

type SerializedError = {
  message?: string;
};

function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return Boolean(
    error &&
      typeof error === "object" &&
      "status" in error &&
      "data" in error,
  );
}

function extractMessageFromData(data: unknown): string | null {
  if (typeof data === "string") {
    return data;
  }

  if (!data || typeof data !== "object") {
    return null;
  }

  const asRecord = data as Record<string, unknown>;

  if (typeof asRecord.message === "string") {
    return asRecord.message;
  }

  if (typeof asRecord.error === "string") {
    return asRecord.error;
  }

  if (Array.isArray(asRecord.message) && asRecord.message.length > 0) {
    const first = asRecord.message[0];
    return typeof first === "string" ? first : null;
  }

  return null;
}

export function getApiErrorMessage(
  error: unknown,
  fallback = "Something went wrong. Please try again.",
) {
  const canExposeBackendMessage = process.env.NODE_ENV !== "production";

  if (isFetchBaseQueryError(error)) {
    const message = extractMessageFromData(error.data);
    if (canExposeBackendMessage && message) {
      return message;
    }
    return fallback;
  }

  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as SerializedError).message === "string"
  ) {
    const message = (error as SerializedError).message ?? null;
    if (canExposeBackendMessage && message) {
      return message;
    }
    return fallback;
  }

  return fallback;
}
